import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { AboutStats } from '../types';

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const stats: AboutStats = {
    smartContracts: 10,
    yearsExperience: 9,
    projectsCompleted: 30
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const statVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="about" className="about" ref={ref}>
      <div className="container">
        <motion.h2
          className="section-title"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
        >
          About Me
        </motion.h2>

        <motion.div
          className="about-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="about-text" variants={itemVariants}>
            <motion.p variants={itemVariants}>
              As a Computer Science Master's Candidate, I specialize in Research and Development within 
              the Blockchain Ecosystem. My expertise spans leading technologies including Bitcoin, 
              Ethereum Virtual Machine, Solana, Aptos, and Sui.
            </motion.p>
            
            <motion.p variants={itemVariants}>
              With a passion for technology that began in my youth, I have actively contributed to the 
              development of various decentralized applications (DApps), including NFTs, DeFi platforms, 
              and Play-to-Earn (P2E) games. My work encompasses the full spectrum of blockchain development, 
              from smart contract architecture to frontend integration and security audits.
            </motion.p>
            
            <motion.p variants={itemVariants}>
              Currently, my efforts are directed towards innovation in trading bot development, 
              specifically in areas like copy-trading, volume-bot, bundler-bot, and arbitrage-bot 
              solutions. I am also deeply engaged in pioneering projects at the intersection of AI 
              and blockchain, particularly in the creation of AI Agents for meme coin launches.
            </motion.p>
            
            <motion.p variants={itemVariants}>
              I possess robust business communication skills in English and have accumulated substantial 
              experience in remote working environments, ensuring effective collaboration and project 
              management across diverse teams.
            </motion.p>

            <motion.div
              className="stats"
              variants={containerVariants}
            >
              <motion.div
                className="stat"
                variants={statVariants}
                whileHover={{ scale: 1.05 }}
              >
                <motion.h3
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  {stats.smartContracts}+
                </motion.h3>
                <p>Smart Contracts Deployed</p>
              </motion.div>

              <motion.div
                className="stat"
                variants={statVariants}
                whileHover={{ scale: 1.05 }}
              >
                <motion.h3
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.7, duration: 0.5 }}
                >
                  {stats.yearsExperience}+
                </motion.h3>
                <p>Years Experience</p>
              </motion.div>

              <motion.div
                className="stat"
                variants={statVariants}
                whileHover={{ scale: 1.05 }}
              >
                <motion.h3
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                >
                  {stats.projectsCompleted}+
                </motion.h3>
                <p>Projects Completed</p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div className="about-image" variants={itemVariants}>
            <motion.div
              className="profile-card"
              whileHover={{ scale: 1.05, rotateY: 5 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="profile-avatar"
                animate={{
                  y: [0, -6, 0],
                  scale: [1, 1.02, 1]
                }}
                transition={{
                  y: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                  scale: { duration: 3, repeat: Infinity, ease: "easeInOut" }
                }}
                whileHover={{ scale: 1.06, y: -2 }}
              >
                <img
                  src="/images/hodlwarden.png"
                  alt="Profile avatar"
                  className="profile-avatar-img"
                />
              </motion.div>
              <div className="profile-info">
                <h3>Full-stack Web3 AI Dev</h3>
                <p>Available for Projects</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;

